import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react'
import autoTable from 'jspdf-autotable';
import jsPDF from "jspdf";
import moment from "moment";

import { Table, TableBody, TableContainer, Paper } from "@mui/material";

import { callAddFontNirmalaBold } from '../../../styles/fonts/Nirmala-bold';
import { callAddFontNirmalaNormal } from '../../../styles/fonts/Nirmala-normal';
import { excelConfig, filterHeaderXlStyle, filterHeaderXlDataStyle } from '../../../configs/excelConfig';
import { fetchApi } from '../../../services/apiService';
import { hasAccess, jsPdfHeaderFooter, jsPdfDidParseCellCallback, jsPdfWillDrawCellCallback } from '../../../components/generics/helper';
import { Loader } from '../../../components/generics/Loader';
import { REPORT_TABLE_PAGINATION_ROWS_OPTIONS, EXPORT_PER_REQUEST_RECORD_LIMIT, REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED } from "../../../constants/commonConstants";
import { StyledTableRow, StyledTableCell } from "../../../components/Table/TableStyle";
import CommonTableHeaderRow from '../../../components/Table/CommonTableHeaderRow';
import CommonTablePagination from '../../../components/Table/CommonTablePagination';
import excelExport from '../../../services/exports/export-excel';
import TyreReportPopup from './TyreReportPopup';

const nowrapCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};
const TyreReportList = (props) => {
    const { tyreReportFilters, loadData, setLoadData } = props
    const { enqueueSnackbar } = useSnackbar();

    const { selectedRolesRights = [], adminType = 0 } = useSelector((state) => state.login?.userData);
    const userToken = useSelector((state) => state.login.userToken);
    const userData = useSelector((state) => state.login.userData);

    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [perPageRecords, setPerPageRecords] = useState(REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED);

    const getTyreReportParams = (filters, page = 1, perPage = EXPORT_PER_REQUEST_RECORD_LIMIT) => {
        let params = {};
        if (filters?.searchVehIds !== '') {
            params = { ...params, searchTRVehicleIds: filters?.searchVehIds };
        }
        if (filters?.searchTyrePositionValues !== '') {
            params = { ...params, searchTRTyrePositions: filters?.searchTyrePositionValues };
        }
        if (filters?.searchCategoryIds !== '') {
            params = { ...params, searchTRItemCategoryIds: filters?.searchCategoryIds };
        }
        if (filters?.searchSubCategoryIds !== '') {
            params = { ...params, searchTRItemSubCategoryIds: filters?.searchSubCategoryIds };
        }
        if (filters?.searchBrandIds !== '') {
            params = { ...params, searchTRItemBrandIds: filters?.searchBrandIds };
        }
        if (filters?.searchItemIds !== '') {
            params = { ...params, searchTRItemIds: filters?.searchItemIds };
        }
        if (filters?.searchSerialNos !== '') {
            params = { ...params, searchTRSearialNos: filters?.searchSerialNos };
        }
        params.pageNo = page;
        params.perPageRecords = perPage;
        params.infoSerialNosFor = 1;
        return params;
    };

    const generateTyreReportData = async (page = 0, rowsPerPage = perPageRecords) => {
        setIsLoading(true);
        try {
            const searchParams = getTyreReportParams(tyreReportFilters, page + 1, rowsPerPage);

            const response = await fetchApi('reports/getTyreReportData', searchParams, userToken);
            setIsLoading(false);

            if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                if (response.data.tyreReportData.length > 0) {
                    enqueueSnackbar('Tyre Report Data Loaded Successfully', { variant: 'success', key: Date.now() });
                    setTableData(response?.data?.tyreReportData);
                    setTotalRecords(response.data.totalRecords);
                } else {
                    enqueueSnackbar('Ooops! No Record Found', { variant: 'error' });
                    setTableData([]);
                }
            } else {
                enqueueSnackbar(response.errorMessage, { variant: 'error' });
                setTableData([]);
                setTotalRecords(0);
            }
        } catch (err) {
            let message = 'Tyre Report List Loading Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            setTableData([])
            setTotalRecords(0);
        } finally {
            setLoadData(false);
        }
    }

    const exportAllTyreReportData = async () => {
        const allTRData = [];
        try {
            const params = getTyreReportParams(tyreReportFilters);
            const totalPages = Math.ceil(totalRecords / params.perPageRecords);
            for (let page = 1; page <= totalPages; page++) {
                const pageParams = { ...params, pageNo: page };
                const response = await fetchApi('reports/getTyreReportData', pageParams, userToken);
                if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                    allTRData.push(...response.data.tyreReportData);
                }
            }
            return allTRData;
        } catch (err) {
            let message = 'Tyre Report List Export Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            return [];
        }
    }

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
        generateTyreReportData(newPage, perPageRecords);
    };
    const handleChangeRowsPerPage = (event) => {
        setPerPageRecords(parseInt(event.target.value, 10));
        setPageNo(0);
        generateTyreReportData(0, parseInt(event.target.value, 10));
    };

    const columnDefs = [
        { headerName: 'Vehicle Number', pdfHead: 'Vehicle Number', field: 'vehicleNo', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Vehicle Code', pdfHead: 'Vehicle Code', field: 'vehicleCode', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'No. of Tyre', pdfHead: 'Number of Tyre', field: 'noOfTyres', xlCol: { dataType: 'number', dataFormatter: null, width: 20 }, headerSx: { width: '80px', textAlign: "center" }, allowSorting: false },
        { headerName: 'Tyre Position', pdfHead: 'Tyre Position', field: 'position', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '90px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Category', pdfHead: 'Category', field: 'catgoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '130px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Subcategory', pdfHead: 'Sub category', field: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Brand of Tyre', pdfHead: 'Tyre Brand', field: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Item Name', pdfHead: 'Item Name', field: 'itemName', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Serial No', pdfHead: 'Serial No', field: 'serialNo', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false, },
        { headerName: 'Tyre KM', pdfHead: 'Tyre KM', field: '', xlCol: { dataType: 'number', dataFormatter: null, width: 30 }, headerSx: { width: '80px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Last Fitment Date', pdfHead: 'Last Fitment Date', field: 'fitmentDate', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Last JobCard Date', pdfHead: 'Last JobCard Date', field: 'jobCardDate', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
    ];
    const getFilterSectionData = () => {
        return {
            filterHeaderXlStyle,
            filterHeaderXlDataStyle,
            filterData: [
                {
                    columns: [
                        { header: 'Vehicle Number', dataKey: 'vehicleNoWithCode', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Tyre Position', dataKey: 'position', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Category', dataKey: 'catgoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Subcategory', dataKey: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Brand', dataKey: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Item Name', dataKey: 'itemName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Serial No', dataKey: 'serialNo', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                    ],
                    pdfColumnStyles: {
                        0: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        1: { cellWidth: 31, halign: 'left', fontSize: 10 },
                        2: { cellWidth: 45, halign: 'left', fontSize: 10 },
                        3: { cellWidth: 45, halign: 'left', fontSize: 10 },
                        4: { cellWidth: 35, halign: 'left', fontSize: 10 },
                        5: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        6: { cellWidth: 30, halign: 'left', fontSize: 10 },
                    },
                    data: [
                        {
                            vehicleNoWithCode: tyreReportFilters.searchVehIds === "" ? 'All Vehicle No.' : tyreReportFilters.searchVehIds.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchVehIds.split(',').length} ]` : tyreReportFilters?.searchVehNos[0],
                            position: tyreReportFilters.searchTyrePositionValues === "" ? 'All Tyre Positions' : tyreReportFilters.searchTyrePositionValues.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchTyrePositionValues.split(',').length} ]` : tyreReportFilters?.searchTyrePositionLabels[0],
                            catgoryTitle: tyreReportFilters.searchCategoryIds === "" ? 'All Categories' : tyreReportFilters.searchCategoryIds.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchCategoryIds.split(',').length} ]` : (tyreReportFilters?.searchCategoryNames || [])[0],
                            subCategoryTitle: tyreReportFilters.searchSubCategoryIds === "" ? 'All Sub Categories' : tyreReportFilters.searchSubCategoryIds.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchSubCategoryIds.split(',').length} ]` : (tyreReportFilters?.searchSubCategoryNames || [])[0],
                            brandName: tyreReportFilters.searchBrandIds === "" ? 'All Brands' : tyreReportFilters.searchBrandIds.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchBrandIds.split(',').length} ]` : (tyreReportFilters?.searchBrandNames || [])[0],
                            itemName: tyreReportFilters.searchItemIds === "" ? 'All Items' : tyreReportFilters.searchItemIds.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchItemIds.split(',').length} ]` : tyreReportFilters?.searchItemNames[0],
                            serialNo: tyreReportFilters.searchSerialNos === "" ? 'All Serial Nos' : tyreReportFilters.searchSerialNos.split(',').length > 1 ? `Multiple [ ${tyreReportFilters.searchSerialNos.split(',').length} ]` : tyreReportFilters?.searchSerialNos[0],
                        }
                    ]
                },
            ]
        }
    }
    const printReport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            setIsPdfLoaded(false);
            const pdfColumns = columnDefs.map((col) => {
                return { header: col.pdfHead, dataKey: col.field, xlCol: col.xlCol }
            });

            const allData = await exportAllTyreReportData();
            const fullData = allData.filter(row => row.isDataRow);

            jsPDF.API.events.push(["addFonts", callAddFontNirmalaBold]);
            jsPDF.API.events.push(["addFonts", callAddFontNirmalaNormal]);
            const doc = new jsPDF({
                orientation: 'l',
                format: 'a4',
                compress: true,
            });
            var totalPagesExp = '{total_pages_count_string}';
            doc.autoTableSetDefaults({
                headStyles: { fontSize: 6, halign: 'left', fillColor: [30, 89, 167] },
            });
            doc.setProperties({
                title: `tyreReport-${moment().format('DD-MMM-YY-HH-mm')}.pdf`,
                subject: "Tyre Report",
                author: "Maven Tech Labs Pvt. Ltd.",
                keywords: "maven tech, Maintenance system, Tyre Report",
                creator: "MTLPL",
            });
            autoTable(doc, {
                margin: { top: 16, left: 5, right: 5, bottom: 5 },
                columns: filterSectionData.filterData[0].columns,
                body: filterSectionData.filterData[0].data,
                columnStyles: filterSectionData.filterData[0].pdfColumnStyles,
                theme: 'grid',
                tableWidth: 'wrap',
            });
            doc.line(0, doc.lastAutoTable.finalY + 2, doc.getPageWidth(), doc.lastAutoTable.finalY + 2);
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 4,
                tableWidth: 'auto',
                margin: { top: 2, left: 5, right: 5, bottom: 5 },
                alternateRowStyles: {
                    fillColor: [255, 255, 255]
                },
                columns: pdfColumns,
                body: fullData,

                columnStyles: {
                    0: { minCellWidth: 15 },
                    1: { minCellWidth: 15 },
                    2: { minCellWidth: 12, halign: 'center' },
                    3: { minCellWidth: 12 },
                    4: { minCellWidth: 12 },
                    5: { minCellWidth: 12 },
                    6: { minCellWidth: 12 },
                    7: { minCellWidth: 15 },
                    8: { minCellWidth: 12 },
                    9: { minCellWidth: 12, halign: 'right' },
                    10: { minCellWidth: 12 },
                    11: { minCellWidth: 12 },
                },
                theme: 'grid',
                bodyStyles: {
                    fontSize: 6,
                    halign: 'left',
                },
                didParseCell: (data) => {
                    jsPdfDidParseCellCallback(data, pdfColumns,);
                    if (data.row.raw?.isDataRow && data.row.raw?.rowSpan > 1) {
                        data.row.cells[1].rowSpan = data.row.raw.rowSpan;
                        data.row.cells[0].styles.valign = 'middle';
                        data.row.cells[2].rowSpan = data.row.raw.rowSpan;
                        data.row.cells[2].styles.valign = 'middle';
                    }
                },
                willDrawCell: (data) => {
                    jsPdfWillDrawCellCallback(data);
                },
                didDrawPage: function (data) {
                    data.settings.margin.top = 16;
                    const reportTitle = `Maintenance System | Tyre Report`;
                    jsPdfHeaderFooter(data, reportTitle, userData, totalPagesExp);
                },
            },
            );
            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages(totalPagesExp)
            }
            setIsPdfLoaded(true);
            setTimeout(() => {
                // document.getElementById('output').data = doc.output('datauristring', `tyreReport_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.pdf`);
                const blob = doc.output('blob');
                const url = URL.createObjectURL(blob);
                document.getElementById('output').data = url;
                setIsPdfLoading(false);
            }, 1000);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
            setIsPdfLoaded(false);
        }
    }

    const reportExcelExport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            const xlColumns = columnDefs.map((col) => {
                return { colHeader: col.headerName, field: col.field, xlCol: col.xlCol }
            });
            const reportTitle = `Maintenance System | Tyre Report`
            const xlConfig = {
                ...excelConfig,
                columns: xlColumns,
                fileName: `Tyre_Report_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.xlsx`,
                showFilterSection: true,
                filterSectionData,
                workSheetTitle: `Tyre_Report`,
                headerFooter: {
                    firstHeader: `&L&14&K104FA2 ${reportTitle} &R&B&12 Company Name: ${userData.companyName}`,
                    firstFooter: `&L&12 Report By : ${userData.userFullName}  [ ${userData.branchName} ] &C&12 Report Time :  ${moment(new Date()).format("DD MMM YYYY, ddd, HH:mm")} &R&12 Page: &P of &N`
                },
                sheetHeaderData: [{
                    "caption": "Report Name :",
                    "value": `Tyre Report`,
                    "colSpan": 7
                }, {
                    "caption": "Company Name :",
                    "value": userData.companyName,
                    "colSpan": 6
                }],
            };
            const allData = await exportAllTyreReportData();
            const fullData = allData.filter(row => row.isDataRow);
            await excelExport.processExcel(xlConfig, fullData);
            setIsPdfLoading(false);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
        }
    }

    const backToScreenView = () => {
        setIsPdfLoaded(false);
    }

    useEffect(() => {
        if (loadData) {
            generateTyreReportData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    const handleMoreDetails = (e, row) => {
        e.stopPropagation();
        if (!row.serialNo) return;
        const srNoWiseItemHistoryFilters = {
            searchSerialNo: row.serialNo,
            searchCategoryName: row.catgoryTitle || '-',
            searchSubCategoryName: row.subCategoryTitle || '-',
            searchItemId: row.itemId,
            searchItemName: row.itemName,
            searchBrandName: row.brandName || '-',
            searchVehBrandName: row.vehicleOemName || '-',
            searchVehModelName: row.vehicleModelName || '-',
        }
        setSelectedItemDetails(srNoWiseItemHistoryFilters);
        setShowMoreDetails(true);
    };

    const hideMoreDetails = () => {
        setShowMoreDetails(false);
        setSelectedItemDetails(null);
    }

    return (
        <>
            {isLoading && <div style={{ width: "100%", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
            {tableData && tableData.length > 0 &&
                <>
                    <div className='report--header--container'>
                        <div key="tr_report_name" className='report--for--heading'>Tyre Report</div>
                        <div key="tr_report_action_container" className="report-export-button--container">
                            {!isPdfLoaded && <>
                                {hasAccess(selectedRolesRights, '4132', adminType) && <div key="tr_xls_btn" id="cssEpExCel" tabIndex={0} className="dashed--btn" onClick={() => reportExcelExport()}> EXPORT AS EXCEL </div>}
                                {hasAccess(selectedRolesRights, '4133', adminType) && <div key="tr_xpdf_btn" id="cssEpAsPDF" tabIndex={0} className="dashed--btn" onClick={() => printReport()}> EXPORT AS PDF </div>}
                                {hasAccess(selectedRolesRights, '4131', adminType) && <div key="tr_prn_btn" id="cssEpExPDF" tabIndex={0} className="dashed--btn" onClick={() => printReport()}> PRINT </div>} </>}
                            {isPdfLoaded && <><div key="tr_backBtn" className="dashed--btn" onClick={() => backToScreenView()}> Back </div> </>}
                            {isPdfLoading && <div key="tr_loading_container" style={{ width: "100px", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
                        </div>
                    </div>
                    {!isPdfLoaded && !isPdfLoading && (
                        <>
                            <TableContainer component={Paper} className="jc--scroll jobCrdPreviewModel" style={{ maxHeight: '85vh' }} >
                                <Table id="tyreReport" stickyHeader aria-label="sticky table" sx={{ minWidth: 1320 }} >
                                    <CommonTableHeaderRow columnDefs={columnDefs} />
                                    <TableBody>
                                        {tableData && tableData.length > 0 ? (
                                            tableData.map((row, index) => {
                                                return (
                                                    <StyledTableRow key={`${row.rowType}-D-${index}`} className="grid" >
                                                        {row.rowSpanColumns?.includes(1) && (
                                                            <StyledTableCell rowSpan={row.rowSpan} style={{ ...nowrapCellStyle }}> {row.vehicleNo} </StyledTableCell>
                                                        )}
                                                        {row.rowSpanColumns?.includes(2) && (
                                                            <StyledTableCell rowSpan={row.rowSpan} style={{ ...nowrapCellStyle }}>{row.vehicleCode}</StyledTableCell>
                                                        )}
                                                        {row.rowSpanColumns?.includes(3) && (
                                                            <StyledTableCell rowSpan={row.rowSpan} align="center" style={{ ...nowrapCellStyle }}> {row.noOfTyres} </StyledTableCell>
                                                        )}
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.position}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.catgoryTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.subCategoryTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.brandName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.itemName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }} className="text" tabIndex={0} onClick={(e) => handleMoreDetails(e, row)}>{row.serialNo || ''}</StyledTableCell>
                                                        <StyledTableCell align="right" style={{ ...nowrapCellStyle }}> {''} </StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}> {''} </StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}> {row.jobCardDate} </StyledTableCell>
                                                    </StyledTableRow>
                                                );
                                            })
                                        ) : (
                                            <StyledTableRow key="no-records">
                                                <StyledTableCell colSpan={columnDefs.length} align="center">
                                                    <div className="no--records">No Records</div>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div className="report-pagination--container">
                                <CommonTablePagination
                                    rowsPerPageOption={REPORT_TABLE_PAGINATION_ROWS_OPTIONS}
                                    count={totalRecords}
                                    rowsPerPage={perPageRecords}
                                    className=""
                                    page={pageNo}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </div>
                        </>
                    )}
                </>
            }
            {isPdfLoaded && <object id="output" type="application/pdf" width="100%" height="600" title="MTLPL - Maintenance System - Tyre Report" aria-label="MTLPL - Maintenance System - Tyre Report"></object>}
            {showMoreDetails && selectedItemDetails && <TyreReportPopup handleModal={hideMoreDetails} selectedItemDetails={selectedItemDetails} />}
        </>
    );
}
export default TyreReportList;